import React, { useState, useEffect, FormEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import MotiveLogo from 'assets/logos/motive-logo.svg'
import Project44Logo from 'assets/logos/project44-small-logo.png'
import moment from 'moment'
import InfoIcon from 'transfix-icons/build/InfoIcon'
import { Button } from 'transfix-ui/components/Button'
import { FormModal } from 'transfix-ui/components/Modals'
import { Radio } from 'transfix-ui/components/Radio'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { Box } from 'transfix-ui/core'
import {
  isFetchingEldTracking,
  selectEldTrackingError,
  selectEldTracking,
  isEldTrackingSubmitSuccess,
} from 'utils/reducers/carrier'
import { ELD_STATUS } from 'apps/carrier/constants/carrierProfile'
import { editEldTracking, checkEldTrackingMethod } from 'apps/carrier/utils/actions/carrierInfo'
import { useTrackingLocationStyles } from './useTrackingLocationStyles'

interface ITrackingLocationModal {
  showModal: boolean
  setShowModal: (show: boolean) => void
}

function TrackingLocationModal({ setShowModal, showModal }: ITrackingLocationModal) {
  const dispatch = useDispatch()
  const isLoading = useSelector(isFetchingEldTracking)
  const errors = useSelector(selectEldTrackingError)
  const {
    eldTrackingMethod,
    isEldApproved,
    isEldApprovedUpdatedAt,
    keepTruckinEnabled,
    keepTruckinEnabledUpdatedAt,
  } = useSelector(selectEldTracking)
  const isSubmitSuccess = useSelector(isEldTrackingSubmitSuccess)
  const [enableProject44, setEnableProject44] = useState<boolean>(Boolean(isEldApproved))
  const { id } = useParams<{ id: string }>()
  const classes = useTrackingLocationStyles()
  const saveELDTracking = () =>
    dispatch(editEldTracking({ carrierId: id!, isEldApproved: enableProject44 }))
  const handleOptionChange = ({ currentTarget }: FormEvent<HTMLInputElement>) => {
    setEnableProject44(Boolean(Number(currentTarget.value)))
  }
  const closeModalHandler = () => setShowModal(false)
  const clickCheckStatusHandler = () => dispatch(checkEldTrackingMethod(id!))

  useEffect(() => {
    if (isSubmitSuccess && !isLoading) {
      setShowModal(false)
    }
  }, [isSubmitSuccess, isLoading, setShowModal])

  return (
    <FormModal
      title='Tracking Capabilities'
      size={6}
      open={showModal}
      error={errors}
      dataTest='project44-modal'
      onClose={closeModalHandler}
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          disabled: isLoading,
          dataTest: 'cancel-project44',
        },
        {
          label: 'Save',
          type: 'confirm',
          disabled: isLoading,
          dataTest: 'save-project44',
          action: saveELDTracking,
        },
      ]}>
      <section className='radio-options'>
        <div className='flex flex-column'>
          <div className={classes.subContent}>
            <img className={classes.trackingLogo} src={Project44Logo} alt='' />
            <span className={classes.textBold}>project44</span>
          </div>
          <div data-test='eld-tracking-method'>
            {eldTrackingMethod && <Box>{ELD_STATUS[eldTrackingMethod]}</Box>}
            {isEldApprovedUpdatedAt && (
              <span className={classes.textSmall}>
                Last updated {moment(isEldApprovedUpdatedAt).fromNow()}
              </span>
            )}
          </div>
          <Button
            onClick={clickCheckStatusHandler}
            className='btn-text-link tl-ns mb4 mt3'
            data-test='check-eld-tracking-method'>
            Check for Status Updates
          </Button>
        </div>
        <div className='flex items-center'>
          <p className={classes.textCaption}>Onboarded &amp; approved for project44 Tracking</p>
          <Tooltip
            id='project44'
            position='top'
            trigger={
              <div className='info-icon-wrapper ml1'>
                <InfoIcon color='coolGray5' />
              </div>
            }>
            <p className='tc'>
              Transfix pays per shipment, so
              <br /> carriers should be marked
              <br /> eligible only if they generally
              <br />
              run loads that require tracking
            </p>
          </Tooltip>
        </div>
        <div className='flex'>
          <Radio
            id='project44-yes'
            name='project44-yes'
            dataTest='project44-yes'
            value='1'
            label='Yes'
            checked={enableProject44}
            onChange={handleOptionChange}
            RadioProps={{
              size: 'small',
            }}
          />
          <Radio
            id='project44-no'
            name='project44-no'
            dataTest='project44-no'
            value='0'
            label='No'
            checked={!enableProject44}
            onChange={handleOptionChange}
            RadioProps={{
              size: 'small',
            }}
          />
        </div>
      </section>
      <section>
        <hr />
        <div className={classes.subContent}>
          <img className={classes.trackingLogo} src={MotiveLogo} alt='' />
          <span className={classes.textBold}>Motive</span>
        </div>
        <div className='flex flex-column'>
          {keepTruckinEnabled && (
            <div>
              <div className='flex items-center'>
                <p>This carrier is connected to Motive</p>
              </div>
              {keepTruckinEnabledUpdatedAt && (
                <span className={classes.textSmall}>
                  Last updated {moment(keepTruckinEnabledUpdatedAt).fromNow()}
                </span>
              )}
            </div>
          )}
          {!keepTruckinEnabled && (
            <Box>
              <span className={classes.textSmall}>Not connected</span>
            </Box>
          )}
        </div>
      </section>
    </FormModal>
  )
}

export default TrackingLocationModal
